import { RootState } from '../../app/store'
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { CompTableData } from '../utils/CompTable'
import { API_BASE, IS_LITE, LITE_TOKEN } from '../../app/const'
import { fireauth } from '../auth/AuthSlice'
import { ThemesType, customAppTheme } from '../utils/MUITheme'
import { Customize } from '../customize/CustomizeSlice'

export interface AnalysisState {
  status: 'idle' | 'loading' | 'failed'
  marketingMaterialStatus: 'idle' | 'loading' | 'failed'
  marketingMaterial: any
  data: any
  fault: any
  onlySlug?: boolean
  slug?: string
  clearCacheStatus: 'idle' | 'loading' | 'failed'
  domain?: string
  domainCss?: ThemesType
}
const initialState: AnalysisState = {
  status: 'idle',
  marketingMaterialStatus: 'idle',
  marketingMaterial: '',
  data: '',
  fault: '',
  clearCacheStatus: 'idle',
  domain: undefined,
  domainCss: customAppTheme(undefined)
}

export const fetchAnalysisSlugForAddressAsync = createAsyncThunk('analysis/fetchSlugForAddr', async ({ slugOrAddress, compCount, propertyType, beds, baths, sqft, lat, lng }: { slugOrAddress: string, compCount: number, propertyType: string, beds: string, baths: string, sqft: string, lat: string, lng: string }, { getState }) => {
  const state = getState() as RootState
  return axios.post(`${API_BASE}apps/mini/estimate`, {
    address: slugOrAddress,
    compCount,
    propertyType,
    bedrooms: beds,
    bathrooms: baths,
    squareFootage: sqft,
    latitude: lat,
    longitude: lng
  }, {
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      "Authorization": `Bearer ${IS_LITE ? LITE_TOKEN : await fireauth.currentUser?.getIdToken()}`
    }
  })
})

export const clearCacheForAddressAsync = createAsyncThunk('analysis/clearCacheForAddressAsync',
  async ({ slugOrAddress, compCount, propertyType, beds, baths, sqft }: { slugOrAddress: string, compCount: number, propertyType: string, beds: string, baths: string, sqft: string }, { getState }) => {
    const state = getState() as RootState
    return axios.post(`${API_BASE}apps/clearCache/estimate`, {
      address: slugOrAddress,
      compCount,
      propertyType,
      bedrooms: beds,
      bathrooms: baths,
      squareFootage: sqft
    }, {
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${IS_LITE ? LITE_TOKEN : await fireauth.currentUser?.getIdToken()}`
      }
    })
  })

export const fetchAnalysisDataForSlugAsync = createAsyncThunk('analysis/fetchDataForSlug', async ({ slug, domain }: { slug: string, domain: string | undefined }, { getState }) => {
  const state = getState() as RootState
  return domain === undefined && state.auth.isAuthenticated ? axios.get(`${API_BASE}apps/estimate/${slug}`, {
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      "Authorization": `Bearer ${IS_LITE ? LITE_TOKEN : await fireauth.currentUser?.getIdToken()}`
    }
  }) : axios.get(`${API_BASE}app/estimate/${slug}/${domain ? domain : 'rf'}`, {
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      ...(IS_LITE && { "Authorization": `Bearer ${LITE_TOKEN}` })
    }
  })
})

export const fetchMarketingMaterialForSlugAsync = createAsyncThunk('analysis/fetchMarketingMaterialForSlug', async ({ slug }: { slug: string }, { getState }) => {
  const state = getState() as RootState
  return axios.get(`${API_BASE}apps/marketing/${slug}`, {
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      "Authorization": `Bearer ${IS_LITE ? LITE_TOKEN : await fireauth.currentUser?.getIdToken()}`
    }
  })
})

export const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    clearSlug: (state) => {
      console.log(`clearing slug ${state.slug}`)
      state.data = initialState.data
      state.slug = initialState.slug
      state.onlySlug = initialState.onlySlug
      state.fault = initialState.fault
      state.status = initialState.status
      state.marketingMaterial = initialState.marketingMaterial
      state.marketingMaterialStatus = initialState.marketingMaterialStatus
      state.domain = initialState.domain
      state.domainCss = initialState.domainCss
    },
    setDomain: (state, action: PayloadAction<string | undefined>) => {
      state.domain = action.payload
      state.domainCss = customAppTheme(action.payload)
    },
    setDomainCss: (state, action: PayloadAction<Customize>) => {
      if (state.domainCss) {
        state.domainCss.toolbarBG = action.payload.secondaryColor
        state.domainCss.buttonBG = action.payload.secondaryColor
      }
    }
  }, extraReducers: (builder) => {
    builder.addCase(fetchAnalysisDataForSlugAsync.pending, (state) => {
      state.status = 'loading'
    }).addCase(fetchAnalysisDataForSlugAsync.fulfilled, (state, action) => {
      state.status = 'idle'
      const tmp = (action.payload as any).data
      if (tmp?.comps?.listings) {
        tmp.comps.listings = tmp.comps.listings.map((elem: any) => Object.assign({}, elem, { ppsqft: elem.squareFootage ? parseFloat((elem.price / elem.squareFootage).toFixed(2)) : 0 }))
      }
      state.data = tmp
      state.fault = ''
    }).addCase(fetchAnalysisDataForSlugAsync.rejected, (state, action) => {
      state.status = 'idle'
      state.data = ''
      state.onlySlug = false
      state.slug = ''
      state.fault = action.error.message
    }).addCase(fetchAnalysisSlugForAddressAsync.pending, (state) => {
      state.status = 'loading'
    }).addCase(fetchAnalysisSlugForAddressAsync.fulfilled, (state, action) => {
      state.status = 'idle'
      const tmp = (action.payload as any).data
      if (tmp && tmp.fault) {
        state.fault = `${tmp.faultCode}: ${tmp.faultMessage}`
        state.slug = `${tmp.faultCode}`
        state.onlySlug = false
      } else {
        state.slug = tmp
        state.onlySlug = true
        state.fault = ''
      }
    }).addCase(fetchAnalysisSlugForAddressAsync.rejected, (state, action) => {
      state.status = 'idle'
      state.onlySlug = false
      const { slugOrAddress } = action.meta.arg;
      state.slug = `error_${slugOrAddress}`//'error happened. Please try after sometime.'
      state.fault = action.error.message
    }).addCase(fetchMarketingMaterialForSlugAsync.pending, (state) => {
      state.marketingMaterialStatus = 'loading'
    }).addCase(fetchMarketingMaterialForSlugAsync.fulfilled, (state, action) => {
      state.marketingMaterialStatus = 'idle'
      state.marketingMaterial = (action.payload as any).data
      state.fault = ''
    }).addCase(fetchMarketingMaterialForSlugAsync.rejected, (state, action) => {
      state.marketingMaterialStatus = 'idle'
      state.marketingMaterial = ''
      state.fault = action.error.message
    }).addCase(clearCacheForAddressAsync.pending, (state) => {
      state.clearCacheStatus = 'loading'
    }).addCase(clearCacheForAddressAsync.fulfilled, (state, action) => {
      state.clearCacheStatus = 'idle'
    }).addCase(clearCacheForAddressAsync.rejected, (state, action) => {
      state.clearCacheStatus = 'failed'
    })
  }
})

export default analysisSlice.reducer
export const selectAnalysisSlug = (state: RootState) => state.analysis.slug
export const selectAnalysisData = (state: RootState) => state.analysis.data
export const selectAnalysisFault = (state: RootState) => state.analysis.fault
export const selectChatGenesis = (state: RootState) => state.analysis.data?.chatGenesis as any
export const selectAnalysisStatus = (state: RootState) => state.analysis.status
export const selectClearCacheStatus = (state: RootState) => state.analysis.clearCacheStatus
export const selectMarketingMaterial = (state: RootState) => state.analysis.marketingMaterial
export const selectMarketingMaterialStatus = (state: RootState) => state.analysis.marketingMaterialStatus
export const selectDomain = (state: RootState) => state.analysis.domain
export const selectDomainCss = (state: RootState) => state.analysis.domainCss
export const { clearSlug, setDomain, setDomainCss } = analysisSlice.actions

